import { StrictMode } from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { AuthProvider, initSentry, Sentry } from '@org/ui';
import { installDomResilience } from '@org/utils-client';
import { ensureAdminI18n } from './i18n';

installDomResilience();

initSentry({
  app: 'admin',
  dsn: import.meta.env.VITE_SENTRY_DSN ?? import.meta.env.VITE_VITE_SENTRY_DSN,
  environment: import.meta.env.VITE_ENV ?? import.meta.env.MODE,
  release: import.meta.env.VITE_SENTRY_RELEASE,
});

// Import the generated route tree
import { routeTree } from './routeTree.gen';

// Import styles
import './styles.css';

// Create a new router instance
const router = createRouter({ routeTree });

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

// Render the app
const rootElement = document.getElementById('root')!;

const renderApp = () => {
  if (rootElement.innerHTML) {
    return;
  }

  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <StrictMode>
      <AuthProvider>
        <Sentry.ErrorBoundary fallback={<RootErrorFallback />}>
          <RouterProvider router={router} />
        </Sentry.ErrorBoundary>
      </AuthProvider>
    </StrictMode>,
  );
};

function RootErrorFallback() {
  return (
    <div style={{ padding: 24, fontFamily: 'system-ui, sans-serif' }}>
      <h2>Something went wrong</h2>
      <p>The page crashed. Our team has been notified. Please refresh to try again.</p>
    </div>
  );
}

ensureAdminI18n()
  .then(renderApp)
  .catch((error) => {
    console.error('Failed to initialize translations', error);
    renderApp();
  });
